import React, { useState, useEffect } from "react";
import { Card, Row, Col, Typography, Alert, Button, Tooltip, Spin } from "antd";
import { LikeOutlined, DislikeOutlined } from "@ant-design/icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useContext } from "react";
import { ProfileContext } from "Common/hooks/context/ProfileContext";
const PersonalityAssessmentForm = () => {
  const { isProfileComplete } = useContext(ProfileContext);
  const isBlocked = !isProfileComplete;
  const [questionGroups, setQuestionGroups] = useState([]);
  const [answers, setAnswers] = useState({});
  const [errorGroup, setErrorGroup] = useState(null);
  const navigate = useNavigate();
  const [allowAssessment, setAllowAssessment] = useState(false);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");
  const { Title } = Typography;
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const token = localStorage.getItem("token");
        const learnerId = localStorage.getItem("personalId");
        const reportResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/PersonalityAssessment/Learner/Report`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: { learnerId }
          }
        );
        if (reportResponse.data?.hasAnyReport) {
          setAllowAssessment(false);
        } else {
          const questionsResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/PersonalityTrait/Box/List`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          setQuestionGroups(questionsResponse.data);
          setAllowAssessment(true);
        }
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);  // Set loading to false at the end
      }
    };
    fetchInitialData();
  }, []);
  const handleIconClick = (boxNumber, type, index, traitId) => {
    if (isBlocked) return;
    const current = answers[boxNumber] || {};
    if ((type === "like" && current.dislike === traitId) ||
      (type === "dislike" && current.like === traitId)) {
      setErrorGroup(boxNumber);
      return;
    }
    setErrorGroup(null);
    setAnswers((prev) => ({
      ...prev,
      [boxNumber]: {
        ...prev[boxNumber],
        [type]: traitId
      },
    }));
  };
  const handleSubmit = async () => {
    if (isBlocked) return;
    const learnerId = parseInt(localStorage.getItem("userId"));
    // Validate we have all required data
    if (!learnerId) {
      alert("User ID not found. Please login again.");
      return;
    }
    // Ensure all boxes are completed
    const completedBoxes = Object.keys(answers).length;
    if (completedBoxes !== questionGroups.length) {
      alert(`Please complete all ${questionGroups.length} boxes before submitting`);
      return;
    }
    // Prepare the box responses
    const boxResponses = questionGroups.map(group => {
      const boxAnswer = answers[group.boxNumber];
      if (!boxAnswer || !boxAnswer.like || !boxAnswer.dislike) {
        alert(`Please complete box ${group.boxNumber}`);
        return null;
      }
      return {
        personalityTraitBoxId: parseInt(group.boxNumber),
        likedTraitId: boxAnswer.like,
        dislikedTraitId: boxAnswer.dislike
      };
    }).filter(Boolean);
    // Check if we have all responses
    if (boxResponses.length !== questionGroups.length) {
      return;
    }
    const payload = {
      learnerId,
      boxResponses
    };
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token not found");
      }
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/PersonalityAssessment/Create`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      // Check for successful response
      if (response.status === 200 || response.status === 201) {
        alert("Assessment submitted successfully");
        navigate("/personality-assessment-report"); // Navigate directly without setTimeout
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error) {
      console.error("Submission error:", error);
      let errorMessage = "Submission failed. Please try again.";
      if (error.response) {
        // Handle specific error messages from server
        errorMessage = error.response.data?.message || errorMessage;
        console.error("Server response:", error.response.data);
      }
      alert(errorMessage);
    }
  };
  if (loading) {
    return (
      <Card>
        <Spin tip="Loading assessment..." style={{ width: "100%" }} />
      </Card>
    );
  }
  return (
    <Card
      title="DISC (Personality Assessment)"
      className="card-professional"
      extra={
        <Button type="default" onClick={() => navigate(-1)}>
          Back
        </Button>
      }
    >
      <div
        style={{
          pointerEvents: isBlocked ? "none" : "auto",
          opacity: isBlocked ? 0.6 : 1,
        }}
      >
        {allowAssessment ? (
          <div style={{ padding: "20px", lineHeight: 1.6, fontSize: "15px" }}>
            <h2 style={{ fontSize: "22px" }}>DISC Personality Assessment</h2>
            <p>
              This free DISC personality test lets you assess your DISC type and profile quickly. Find out how the DISC factors — <strong>Dominance, Influence, Steadiness,</strong> and <strong>Compliance</strong> — explain your behavior towards others and everyday activities.
            </p>
            <ul>
              <li><strong>Dominance (D):</strong> assertive, competitive, goal-driven</li>
              <li><strong>Influence (I):</strong> sociable, enthusiastic, persuasive</li>
              <li><strong>Steadiness (S):</strong> calm, supportive, dependable</li>
              <li><strong>Compliance (C):</strong> analytical, detail-oriented, cautious</li>
            </ul>
            <h4 style={{ fontSize: "18px" }}>Instructions:</h4>
            <ul>
              <li>This assessment contains 28 groups of four statements.</li>
              <li>For each group:
                <ul>
                  <li>Select <strong>one statement that is most like you</strong>.</li>
                  <li>Select <strong>one statement that is least like you</strong>.</li>
                </ul>
              </li>
              <li>Be honest and spontaneous — there are no right or wrong answers.</li>
              <li>It should take about <strong>5 to 10 minutes</strong> to complete.</li>
            </ul>
            <h4 style={{ fontSize: "18px" }}>Sample Statements:</h4>
            <ul>
              <li>I am always cheerful</li>
              <li>I like things to be precise and correct</li>
              <li>I enjoy competition</li>
              <li>I am a very calm and placid person</li>
            </ul>
            <p>
              These statements help determine how you react in different situations based on the DISC model.
            </p>
            <div style={{ padding: "24px" }}>
              <Title level={4}>DISC Personality Assessment</Title>
              <Row gutter={[16, 16]}>
                {questionGroups.map((group) => (
                  <Col span={12} key={group.boxNumber}>
                    <div style={{ padding: "8px", border: "1px solid #eee", borderRadius: 4 }}>
                      <Row gutter={[8, 8]}>
                        {group.traits.map((trait, index) => (
                          <Col span={24} key={trait.id}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                border: "1px solid #ccc",
                                padding: "8px 12px",
                                borderRadius: 4,
                                backgroundColor: "#fafafa",
                                fontSize: "15px"
                              }}
                            >
                              <span>{trait.description}</span>
                              <div style={{ display: "flex", gap: "12px" }}>
                                <LikeOutlined
                                  onClick={() =>
                                    handleIconClick(group.boxNumber, "like", index, trait.id)
                                  }
                                  style={{
                                    fontSize: 18,
                                    color:
                                      answers[group.boxNumber]?.like === trait.id ? "green" : "#ccc",
                                    cursor: "pointer"
                                  }}
                                />
                                <DislikeOutlined
                                  onClick={() =>
                                    handleIconClick(group.boxNumber, "dislike", index, trait.id)
                                  }
                                  style={{
                                    fontSize: 18,
                                    color:
                                      answers[group.boxNumber]?.dislike === trait.id ? "red" : "#ccc",
                                    cursor: "pointer"
                                  }}
                                />
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                      {errorGroup === group.boxNumber && (
                        <Alert
                          type="warning"
                          message="You cannot select the same option for both Like and Dislike"
                          style={{ marginTop: 12 }}
                        />
                      )}
                    </div>
                  </Col>
                ))}
              </Row>
              <div style={{ textAlign: "right", marginTop: "24px" }}>
                <Tooltip
                  title={isBlocked ? "Complete your profile to submit" : ""}
                >
                  <Button
                    type="primary"
                    size="large"
                    style={{ padding: "6px 24px", fontWeight: 500 }}
                    onClick={handleSubmit}
                    disabled={isBlocked}
                  >
                    Submit Assessment
                  </Button>
                </Tooltip>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ padding: 20, textAlign: "center" }}>
            <p>You have already submitted your DISC assessment.</p>
            <Button
              type="primary"
              onClick={() => navigate("/personality-assessment-report")}
              style={{ marginTop: 16 }}
            >
              View Your Report
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};
export default PersonalityAssessmentForm;



